import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PatientTable, TabType, URGENT_CONDITIONS, type Patient } from "../components/PatientTable";
import { patientsPath, PATIENTS_STATUS_FROM_SLUG } from "@/routes/slugs";
import apiCoreAdapter from "@/hooks/apiCoreAdapter";
import psiPlatformAdapter from "@/hooks/psiPlatformAdapter";
import { URGENT_PATIENTS_WEBHOOK } from "@/hooks/api/config";
import { toast } from "sonner";
import type { Session } from "@/types/session";
import { useContactChannel } from "@/hooks/useContactChannel";
import useToast from "@/hooks/useToast";
import { useNoAgendamientoModal } from "@/components/psychologist/patients/no-agendamiento/useNoAgendamientoModal";
import { activityTracker } from "../../../../../utils/activityTracker";
import {
  OPEN_NO_AGENDAMIENTO_STATE_KEY,
  sessionsGrade,
  type NoAgendamientoModalParams,
  type OpenNoAgendamientoState,
} from "@/components/psychologist/patients/no-agendamiento/noAgendamientoFormSchema";

// A partir de este umbral de pacientes marcados como urgentes SIMULTÁNEAMENTE (activos, no
// histórico) por una misma psicóloga, se avisa al equipo (administracion@) para que revise la
// situación junto a ella. Se dispara cada vez que se cruza el umbral hacia arriba (3ro, 4to,
// 5to...), no solo la primera vez.
const URGENT_PATIENTS_TEAM_THRESHOLD = 3;

// El tag "Derivado" (paciente que llegó por derivación de otra psicóloga) deja de mostrarse
// una vez que completó esta cantidad de sesiones "realizadas" (settled) con la psicóloga que
// lo recibió, contadas desde que la derivación fue aceptada. Ver el enriquecimiento en el
// efecto de carga de pacientes, más abajo.
const DERIVATED_TAG_SETTLED_SESSIONS_THRESHOLD = 3;

/**
 * Notificación a Make al marcar/desmarcar un paciente como urgente. Desactivada: el cambio real
 * ya se persiste vía `apiCoreAdapter.setPatientUrgency` y este webhook es solo un aviso al
 * backoffice.
 *
 * PARA REACTIVAR: poner en `true` y definir `REACT_APP_URGENT_PATIENTS_WEBHOOK` en el `.env`
 * (sin esa var la constante queda en '' y `fetch('')` pega contra la propia página).
 */
const URGENT_PATIENTS_WEBHOOK_ENABLED = false;

// Mismo template de marca (header sólido #4C1D95, acentos #7C3AED, cards #f6f7fb) que se usa
// para los mails de confirmación de cambios de perfil (ver PsychologistProfile.tsx).
const buildUrgentConfirmationEmailHtml = (params: {
  firstName: string;
  patientName: string;
  fechaConfirmacion: string;
  contactEmail: string;
}): string => {
  const { firstName, patientName, fechaConfirmacion, contactEmail } = params;
  const conditionsHtml = URGENT_CONDITIONS.map(
    (condition) => `
                    <tr>
                      <td style="padding:6px 0;font-size:14px;color:#333333;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
                        ✓ ${condition}
                      </td>
                    </tr>`
  ).join("");
  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Confirmación — Paciente marcado como urgente</title>
</head>
<body style="margin:0;padding:0;background-color:#f0f0f0;font-family:Arial,Helvetica,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f0f0f0;padding:32px 0;">
    <tr>
      <td align="center">
        <table width="560" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;border-radius:12px;overflow:hidden;max-width:560px;width:100%;">
          <tr>
            <td align="center" style="background-color:#4C1D95;padding:28px 32px;">
              <p style="margin:0;color:#ffffff;font-size:20px;font-weight:700;letter-spacing:0.3px;font-family:Arial,Helvetica,sans-serif;">
                Paciente marcado como urgente ✅
              </p>
            </td>
          </tr>
          <tr>
            <td style="padding:32px 36px 8px;">
              <p style="margin:0 0 12px;font-size:15px;color:#1a1f36;font-family:Arial,Helvetica,sans-serif;">
                Hola <strong>${firstName}</strong>, ¿cómo estás?
              </p>
              <p style="margin:0 0 24px;font-size:15px;color:#444444;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
                Te enviamos este mail para dejar registro de que el
                <strong>${fechaConfirmacion}</strong>
                marcaste a <strong>${patientName}</strong> como paciente urgente en Psi Platform.
              </p>
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#F5F3FF;border-left:4px solid #7C3AED;border-radius:0 8px 8px 0;margin-bottom:24px;">
                <tr>
                  <td style="padding:16px 20px;">
                    <p style="margin:0;font-size:12px;font-weight:700;color:#7C3AED;text-transform:uppercase;letter-spacing:0.8px;font-family:Arial,Helvetica,sans-serif;">
                      Condiciones aceptadas
                    </p>
                  </td>
                </tr>
              </table>
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f6f7fb;border-radius:10px;margin-bottom:24px;">
                <tr>
                  <td style="padding:22px 24px;">
                    <table width="100%" cellpadding="0" cellspacing="0" border="0">
                      ${conditionsHtml}
                    </table>
                  </td>
                </tr>
              </table>
              <p style="margin:0 0 24px;font-size:14px;color:#555555;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
                Guardá este mail como constancia. Si tenés alguna duda podés escribirnos a
                <a href="mailto:${contactEmail}" style="color:#7C3AED;font-weight:700;text-decoration:none;">${contactEmail}</a>.
              </p>
            </td>
          </tr>
          <tr>
            <td style="padding:8px 36px 28px;">
              <p style="margin:0;font-size:13px;font-style:italic;color:#1a1f36;font-weight:700;font-family:Arial,Helvetica,sans-serif;">
                Equipo Psi Mammoliti
              </p>
            </td>
          </tr>
          <tr>
            <td style="padding:0 36px 16px;">
              <p style="margin:0;font-size:12px;color:#888888;font-family:Arial,Helvetica,sans-serif;line-height:1.5;">
                Por favor no respondas este mensaje, ya que es automático. Cualquier inquietud podés contactarte a
                <a href="mailto:${contactEmail}" style="color:#888888;text-decoration:underline;">${contactEmail}</a>.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};

// Aviso al equipo (administracion@, vía `audience: 'team'` — ver apiCoreAdapter.psychologistNotifyProfileChanges)
// cuando una misma psicóloga acumula 3 o más pacientes marcados como urgentes de forma
// simultánea. Mismo template de marca que el resto de mails de notificación (header #4C1D95,
// acento #7C3AED, card #f6f7fb) — ver buildUrgentConfirmationEmailHtml arriba.
const buildTeamUrgentThresholdEmailHtml = (params: {
  psychologistName: string;
  psychologistEmail: string;
  urgentCount: number;
  patientNames: string[];
}): string => {
  const { psychologistName, psychologistEmail, urgentCount, patientNames } = params;
  const patientsHtml = patientNames.map(
    (name) => `
                    <tr>
                      <td style="padding:6px 0;font-size:14px;color:#333333;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
                        • ${name}
                      </td>
                    </tr>`
  ).join("");
  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Pacientes urgentes activos — a revisar</title>
</head>
<body style="margin:0;padding:0;background-color:#f0f0f0;font-family:Arial,Helvetica,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f0f0f0;padding:32px 0;">
    <tr>
      <td align="center">
        <table width="560" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;border-radius:12px;overflow:hidden;max-width:560px;width:100%;">
          <tr>
            <td align="center" style="background-color:#4C1D95;padding:28px 32px;">
              <p style="margin:0;color:#ffffff;font-size:20px;font-weight:700;letter-spacing:0.3px;font-family:Arial,Helvetica,sans-serif;">
                Pacientes urgentes activos — a revisar
              </p>
            </td>
          </tr>
          <tr>
            <td style="padding:32px 36px 8px;">
              <p style="margin:0 0 12px;font-size:15px;color:#1a1f36;font-family:Arial,Helvetica,sans-serif;">
                Hola equipo,
              </p>
              <p style="margin:0 0 24px;font-size:15px;color:#444444;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
                <strong>${psychologistName}</strong>
                (<a href="mailto:${psychologistEmail}" style="color:#7C3AED;font-weight:700;text-decoration:none;">${psychologistEmail}</a>)
                tiene actualmente <strong>${urgentCount} pacientes marcados como urgentes</strong> de forma simultánea.
                Te compartimos el detalle para que puedan revisar la situación junto a ella.
              </p>
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#F5F3FF;border-left:4px solid #7C3AED;border-radius:0 8px 8px 0;margin-bottom:24px;">
                <tr>
                  <td style="padding:16px 20px;">
                    <p style="margin:0;font-size:12px;font-weight:700;color:#7C3AED;text-transform:uppercase;letter-spacing:0.8px;font-family:Arial,Helvetica,sans-serif;">
                      Pacientes urgentes activos (${urgentCount})
                    </p>
                  </td>
                </tr>
              </table>
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f6f7fb;border-radius:10px;margin-bottom:24px;">
                <tr>
                  <td style="padding:22px 24px;">
                    <table width="100%" cellpadding="0" cellspacing="0" border="0">
                      ${patientsHtml}
                    </table>
                  </td>
                </tr>
              </table>
              <p style="margin:0;font-size:14px;color:#555555;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
                Este aviso se genera automáticamente al cruzar el umbral de ${URGENT_PATIENTS_TEAM_THRESHOLD} o más pacientes urgentes simultáneos para una misma psicóloga.
              </p>
            </td>
          </tr>
          <tr>
            <td style="padding:8px 36px 28px;">
              <p style="margin:0;font-size:13px;font-style:italic;color:#1a1f36;font-weight:700;font-family:Arial,Helvetica,sans-serif;">
                Psi Platform
              </p>
            </td>
          </tr>
          <tr>
            <td style="padding:0 36px 16px;">
              <p style="margin:0;font-size:12px;color:#888888;font-family:Arial,Helvetica,sans-serif;line-height:1.5;">
                Por favor no respondas este mensaje, ya que es automático.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};

// Re-export so consumers of this hook can still access the types
export type { TabType, Patient };

interface RawPatientResult {
  id?: string | null;
  process_id?: string | null;
  name?: string | null;
  surname?: string | null;
  email?: string | null;
  therapy_type?: string | null;
  payment_type?: string | null;
  payment_status?: string | null;
  city?: string | null;
  localidad?: string | null;
  ciudad?: string | null;
  country?: string | null;
  pais?: string | null;
  country_name?: string | null;
  nationality?: string | null;
  nacionalidad?: string | null;
  first_session_date?: string | null;
  last_session_date?: string | null;
  next_session_date?: string | null;
  session_total?: number | null;
  process_reason?: string | null;
  reason?: string | null;
  consultation_reason?: string | null;
  has_derivation?: boolean | null;
  /** Derivación DESCARTADA que esta psicóloga inició para este paciente — no es campo de
   *  api-core, ver comentario en patients.schema.ts (psiplatform-be). */
  has_outgoing_discarded_derivation?: boolean | null;
  is_urgent?: boolean | null;
  urgent_marked_at?: string | null;
  scheduling_eligible?: boolean | null;
  /** Habilitación manual de derivación cargada por Ops desde el backoffice (ver
   *  ProcessDerivationAuthorization en psiplatform-be) — levanta el bloqueo NZ para
   *  este proceso puntual. No es campo de api-core. */
  derivation_enabled?: boolean | null;
  status_buckets?: Array<"first" | "active" | "inactive" | "withoutsession"> | null;
  // Estado real del vínculo paciente-proceso (process_patient.status). Fuente del badge de estado.
  patient_status?: string | null;
}

// Estados de process_patient que se muestran como badge, mapeados desde el `name` del catálogo.
// `return` (obsoleto) y `discarded` (descartado) no se muestran → sin estado.
type PatientStatus = "lead" | "active" | "on_hold" | "inactive" | "transferred";
const VISIBLE_PATIENT_STATUSES: readonly PatientStatus[] = ["lead", "active", "on_hold", "inactive", "transferred"];
const mapPatientStatus = (name?: string | null): PatientStatus | undefined =>
  name && (VISIBLE_PATIENT_STATUSES as readonly string[]).includes(name) ? (name as PatientStatus) : undefined;

const formatEsLong = (iso?: string | null): string | undefined => {
  if (!iso) return undefined;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return undefined;
    const df = new Intl.DateTimeFormat("es-ES", {
      year: "numeric",
      month: "long",
      day: "2-digit",
    });
    const s = df.format(d);
    return s.charAt(0).toUpperCase() + s.slice(1);
  } catch {
    return undefined;
  }
};

const computeSeniority = (iso?: string | null): string | undefined => {
  if (!iso) return undefined;
  try {
    const start = new Date(iso);
    if (Number.isNaN(start.getTime())) return undefined;
    const now = new Date();
    let years = now.getFullYear() - start.getFullYear();
    let months = now.getMonth() - start.getMonth();
    if (months < 0) { years -= 1; months += 12; }
    if (years > 0 && months > 0) {
      return `${years} ${years === 1 ? "año" : "años"}, ${months} ${months === 1 ? "mes" : "meses"}`;
    }
    if (years > 0) return `${years} ${years === 1 ? "año" : "años"}`;
    if (months > 0) return `${months} ${months === 1 ? "mes" : "meses"}`;
    const diffDays = Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays >= 14) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} ${weeks === 1 ? "semana" : "semanas"}`;
    }
    if (diffDays >= 1) return `${diffDays} ${diffDays === 1 ? "día" : "días"}`;
    return "Hoy";
  } catch {
    return undefined;
  }
};


const mapResultToPatient = (r: RawPatientResult): Patient => {
  const fullName = [r?.name, r?.surname].filter(Boolean).join(" ").trim();
  const paymentType = String(r?.payment_type || "").toLowerCase();
  const rawPaymentStatus = String(r?.payment_status || "").toLowerCase();
  const city = r?.city || r?.localidad || r?.ciudad;
  const country = r?.country || r?.pais || r?.country_name;
  const location = [city, country].filter(Boolean).join(", ") || undefined;
  const nationality = r?.nationality || r?.nacionalidad || undefined;
  // Only map to pending/paid — any other value (cancelled, expired, failed) stays undefined
  const paymentStatus: "pending" | "paid" | undefined =
    rawPaymentStatus === "pending" ? "pending" :
    (rawPaymentStatus === "paid" || rawPaymentStatus === "settled") ? "paid" :
    undefined;
  return {
    id: r?.process_id || r?.id || fullName || Math.random().toString(36).slice(2),
    processId: r?.process_id ?? undefined,
    name: fullName || "Sin nombre",
    email: r?.email || undefined,
    service: r?.therapy_type || undefined,
    patientStatus: mapPatientStatus(r?.patient_status),
    location,
    nationality,
    seniority: computeSeniority(r?.first_session_date),
    firstSession: formatEsLong(r?.first_session_date) || undefined,
    lastSession: formatEsLong(r?.last_session_date) || undefined,
    lastSessionDate: r?.last_session_date || null,
    nextSession: formatEsLong(r?.next_session_date) || null,
    nextSessionDate: r?.next_session_date || null,
    totalSessions: Number.isFinite(r?.session_total) ? Number(r.session_total) : 0,
    consultationReason: r?.process_reason || r?.reason || r?.consultation_reason || undefined,
    isReferred: r?.has_derivation ?? false,
    hasUnsuccessfulDerivation: r?.has_outgoing_discarded_derivation ?? false,
    isUrgent: r?.is_urgent ?? false,
    schedulingEligible: r?.scheduling_eligible ?? true,
    derivationEnabled: r?.derivation_enabled ?? false,
    urgentMarkedAt: r?.urgent_marked_at ?? undefined,
    paymentMethod:
      paymentType === "automatic" ? "automatic" : paymentType === "manual" ? "manual" : undefined,
    paymentStatus,
    rawPaymentStatus,
  };
};

// Mapeo + filtros base compartidos por el fetch principal (tab "Todos"/resto, ver el efecto de
// carga) y por el conteo de urgentes activos de onMarkUrgent: mismo criterio de "paciente
// visible" en los dos lugares (estado visible, deduplicado por id, sin leads cancelados).
const mapVisiblePatients = (list: RawPatientResult[]): Patient[] => {
  const seen = new Set<string>();
  return list
    .map(r => mapResultToPatient(r))
    .filter(p => !!p.patientStatus)
    .filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    })
    // Exclude cancelled leads (0 sessions + no upcoming session) from "Nuevos" and "Todos"
    .filter(p => p.patientStatus !== "lead" || p.totalSessions > 0 || !!p.nextSessionDate);
};

interface UsePatientsDataOptions {
  onSchedule?: (processId: string) => void;
}

export interface UsePatientsDataReturn {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  query: string;
  setQuery: (q: string) => void;
  page: number;
  setPage: (p: number) => void;
  patients: Patient[];
  loading: boolean;
  visiblePatients: Patient[];
  tabs: { id: string; label: string }[];
  typeFilter: string;
  setTypeFilter: (f: string) => void;
  pagoFilter: string;
  setPagoFilter: (f: string) => void;
  motivoFilter: string;
  setMotivoFilter: (f: string) => void;
  // Modal de motivo de consulta
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  modalReason: string | undefined;
  modalPatientName: string;
  // Modal de detalle de paciente
  detailPatient: Patient | null;
  setDetailPatient: (patient: Patient | null) => void;
  detailPatientInitialTab: "info" | "sessions" | "derivation" | "clinical_history";
  detailPatientAutoOpenNote: boolean;
  // Refleja en ?tab= el cambio de pestaña hecho dentro del modal de detalle
  onDetailTabChange: (tab: "info" | "sessions" | "derivation" | "clinical_history") => void;
  // Handlers de tabla
  onViewPatient: (patient: Patient) => void;
  onReadConsultation: (reason: string | undefined, patient: Patient) => void;
  onOpenNoSchedule: (patient: Patient) => void;
  onOpenRefer: (patient: Patient) => void;
  onScheduleFromList: (patient: Patient) => void;
  onGoToCalendar: (patient: Patient) => void;
  onAddNote: (patient: Patient) => void;
  onViewNotes: (patient: Patient) => void;
  onViewPatientDerivation: (patient: Patient) => void;
  onMarkUrgent: (patient: Patient) => Promise<void>;
  onUnmarkUrgent: (patient: Patient) => Promise<void>;
  noAgendamientoModalParams: NoAgendamientoModalParams | null;
  closeNoAgendamientoModal: () => void;
}

export function usePatientsData({ onSchedule }: UsePatientsDataOptions = {}): UsePatientsDataReturn {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { useAuth } = require("@/contexts/AuthContext");
  const { user } = useAuth?.() || { user: undefined };
  const navigate = useNavigate();
  const location = useLocation();
  const {
    params: noAgendamientoModalParams,
    open: openNoAgendamientoModal,
    close: closeNoAgendamientoModal,
  } = useNoAgendamientoModal();
  // Compat con `/patients/no-schedule` (ruta vieja, por si quedó linkeada afuera del repo):
  // NoAgendamientoEmbed redirige acá pidiendo, vía location.state, que abramos el popup.
  // Se limpia el state con replace para que un refresh no lo vuelva a abrir.
  useEffect(() => {
    const state = location.state as OpenNoAgendamientoState | null;
    const toOpen = state?.[OPEN_NO_AGENDAMIENTO_STATE_KEY];
    if (!toOpen) return;
    openNoAgendamientoModal(toOpen);
    navigate(location.pathname + location.search, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { status: statusSlug } = useParams<{ status?: string }>();
  // NZ, u O que viene de NZ: único canal es administración/NZ (WhatsApp de NZ, no el de CX de F/N/resto de O).
  const { email: contactEmail } = useContactChannel();
  const { showInfo } = useToast();

  const STATUS_TABS: TabType[] = ["all", "lead", "active", "on_hold", "inactive", "transferred"];
  const [activeTab, setActiveTabRaw] = useState<TabType>(() => {
    if (statusSlug) return PATIENTS_STATUS_FROM_SLUG[statusSlug] ?? "all";
    const s = searchParams.get("status") as TabType | null;
    return s && STATUS_TABS.includes(s) ? s : "all";
  });

  // Link viejo ?status=<tab> sin slug en el path → migrar (replace), preservando el resto de
  // la query (q, patient, tab del modal de detalle).
  useEffect(() => {
    if (statusSlug) return;
    const s = searchParams.get("status") as TabType | null;
    if (s && STATUS_TABS.includes(s) && s !== "all") {
      const next = new URLSearchParams(searchParams);
      next.delete("status");
      const q = next.toString();
      navigate(`${patientsPath(s)}${q ? `?${q}` : ""}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusSlug]);

  // Sincroniza `activeTab` cuando el slug del path cambia externamente (ej. click directo en un
  // link `/patients/nuevos`, o back/forward del navegador).
  useEffect(() => {
    if (!statusSlug) return;
    const fromSlug = PATIENTS_STATUS_FROM_SLUG[statusSlug];
    if (fromSlug && fromSlug !== activeTab) setActiveTabRaw(fromSlug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusSlug]);
  const [typeFilter, setTypeFilter] = useState<string>("all");   // individual | pareja | all
  const [pagoFilter, setPagoFilter] = useState<string>("all");   // pending | paid | all
  const [motivoFilter, setMotivoFilter] = useState<string>("all"); // with | without | all
  const [query, setQuery] = useState(() => searchParams.get("q") ?? "");
  const [page, setPage] = useState(1);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);

  // Modal de motivo de consulta
  const [modalOpen, setModalOpen] = useState(false);
  const [modalReason, setModalReason] = useState<string | undefined>(undefined);
  const [modalPatientName, setModalPatientName] = useState<string>("");

  // Modal de detalle de paciente
  const [detailPatient, setDetailPatientState] = useState<Patient | null>(null);
  const [detailPatientInitialTab, setDetailPatientInitialTab] = useState<"info" | "sessions" | "derivation" | "clinical_history">("info");
  const [detailPatientAutoOpenNote, setDetailPatientAutoOpenNote] = useState(false);

  // --- Sincronización con la URL (deep-linking compartible) ---
  type DetailTab = "info" | "sessions" | "derivation" | "clinical_history";
  const writePatientParams = (processId: string, tab: DetailTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("patient", processId);
      next.set("tab", tab);
      return next;
    }, { replace: true });
  };
  const clearPatientParams = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("patient");
      next.delete("tab");
      return next;
    }, { replace: true });
  };
  // Setter expuesto al componente: al cerrar (null) limpia los params del detalle
  const setDetailPatient = (patient: Patient | null) => {
    setDetailPatientState(patient);
    if (patient) writePatientParams(patient.processId || patient.id, detailPatientInitialTab);
    else clearPatientParams();
  };
  const onDetailTabChange = (tab: DetailTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      return next;
    }, { replace: true });
  };

  // Apertura desde URL: solo una vez al montar, si llega ?patient=<processId>
  const deepLinkInitRef = useRef(false);
  useEffect(() => {
    if (deepLinkInitRef.current) return;
    deepLinkInitRef.current = true;
    const pid = searchParams.get("patient");
    if (!pid) return;
    const tab = searchParams.get("tab") ?? "";
    const validDetailTabs: DetailTab[] = ["info", "sessions", "derivation", "clinical_history"];
    setDetailPatientInitialTab(validDetailTabs.includes(tab as DetailTab) ? (tab as DetailTab) : "info");
    // usePatientDetail(processId) resuelve el resto del paciente dentro del modal
    setDetailPatientState({ id: pid, processId: pid, name: "", totalSessions: 0 } as Patient);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reflejar la búsqueda en ?q= (con debounce para no spamear el historial)
  useEffect(() => {
    const id = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const v = query.trim();
        if (v) next.set("q", v);
        else next.delete("q");
        return next;
      }, { replace: true });
    }, 300);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const setActiveTab = (tab: TabType) => {
    setActiveTabRaw(tab);
    setTypeFilter("all");
    setPagoFilter("all");
    setMotivoFilter("all");
    setPage(1);
    const next = new URLSearchParams(searchParams);
    next.delete("status");
    const q = next.toString();
    navigate(`${patientsPath(tab)}${q ? `?${q}` : ""}`, { replace: true });
  };

  const tabs = useMemo(
    () => [
      { id: "active", label: "Activos" },
      { id: "lead", label: "Nuevos" },
      { id: "on_hold", label: "En pausa" },
      { id: "transferred", label: "Derivación exitosa" },
      { id: "inactive", label: "Inactivos" },
      { id: "all", label: "Todos" },
    ],
    []
  );

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      try {
        // Convert lowercase filter value back to title-case for the API
        const therapyTypeParam = typeFilter !== "all"
          ? typeFilter.charAt(0).toUpperCase() + typeFilter.slice(1)
          : undefined;

        // 1 solo fetch. El estado de cada paciente lo define process_patient.status (patient_status),
        // no los status_buckets calculados: se mapea directo y se muestran solo los que tienen un
        // estado visible (excluye discarded/return/sin estado). El filtro por pestaña se aplica en
        // cliente sobre ese mismo estado (el backend todavía no filtra por patient_status).
        const resp = await apiCoreAdapter.patientsInformation({ page: 1, per_page: 800, ...(therapyTypeParam ? { therapy_type: therapyTypeParam } : {}) });
        if (cancelled) return;
        const list = (Array.isArray(resp?.results) ? resp.results : []) as RawPatientResult[];
        let mapped = mapVisiblePatients(list);
        if (activeTab !== "all") {
          mapped = mapped.filter(p => p.patientStatus === activeTab);
        }

        // Enrich patients with payment data from sessions for "all", "first" and "active".
        // The patientsInformation endpoint doesn't return payment_status reliably for
        // active/inactive patients, so we resolve it from the sessions list. This must run
        // for "active" too: a patient's payment status is informational only there and must
        // never be used to exclude them from the "Activos" tab.
        if ((activeTab === "all" || activeTab === "lead" || activeTab === "active") && mapped.length > 0) {
          try {
            const now = new Date();
            const past = new Date(now);
            past.setMonth(past.getMonth() - 3);
            const future = new Date(now);
            future.setMonth(future.getMonth() + 3);
            const fmt = (d: Date) =>
              `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} 00:00:00`;

            const sessionsResp = await apiCoreAdapter.psychologistSessionsList({
              from: fmt(past),
              to: fmt(future),
              take: 200,
            });
            if (cancelled) return;

            // Priority: paid > pending > undefined — never downgrade a process already marked as paid
            const STATUS_PRIORITY: Record<string, number> = { paid: 2, pending: 1 };
            const resolvePaymentStatus = (s: Session): "pending" | "paid" | undefined => {
              const paymentStatus = String(s.payments?.[0]?.status || "").toLowerCase();
              if (paymentStatus === "paid" || paymentStatus === "settled") return "paid";
              if (paymentStatus === "pending") return "pending";
              // Fall back to session-level status
              const sessionStatus = String(s.status || "").toLowerCase();
              if (sessionStatus === "active") return "paid";
              if (sessionStatus === "incomplete") return "pending";
              return undefined;
            };

            const paymentByProcess = new Map<string, { method?: "automatic" | "manual"; status?: "pending" | "paid" }>();
            for (const s of sessionsResp.data) {
              if (!s.process) continue;
              const payment = s.payments?.[0];
              const gateway = String(payment?.gateway || "").toLowerCase();
              const newStatus = resolvePaymentStatus(s);
              const existing = paymentByProcess.get(s.process);
              const existingPriority = STATUS_PRIORITY[existing?.status ?? ""] ?? 0;
              const newPriority = STATUS_PRIORITY[newStatus ?? ""] ?? 0;
              // Only update if new status is strictly better than existing
              if (!existing || newPriority > existingPriority) {
                paymentByProcess.set(s.process, {
                  method: gateway === "manual" ? "manual" : "automatic",
                  status: newStatus,
                });
              }
            }

            mapped = mapped.map((p) => {
              const pid = p.processId || p.id;
              const paymentInfo = paymentByProcess.get(pid);
              if (!paymentInfo) return p;
              return {
                ...p,
                paymentMethod: p.paymentMethod || paymentInfo.method,
                paymentStatus: p.paymentStatus || paymentInfo.status,
              };
            });
          } catch {
            // Si falla el enriquecimiento, continuar con los datos base
          }

          // Enrich with nationality from `psychologist/patients` — the process-sessions-data
          // endpoint used above doesn't return it, but this sibling endpoint does. Same
          // merge-by-process-id pattern as the payment enrichment above.
          try {
            const patientsResp = await apiCoreAdapter.psychologistPatients();
            if (cancelled) return;

            const nationalityByProcess = new Map<string, string>();
            for (const p of patientsResp.results ?? []) {
              const key = p.process || p.id;
              if (key && p.nationality) nationalityByProcess.set(key, p.nationality);
            }

            mapped = mapped.map((p) => {
              if (p.nationality) return p;
              const nationality = nationalityByProcess.get(p.processId || p.id);
              return nationality ? { ...p, nationality } : p;
            });
          } catch {
            // Si falla el enriquecimiento, continuar sin nacionalidad (cae en "—")
          }
        }

        // El tag "Derivado" deja de mostrarse una vez que el paciente completó
        // DERIVATED_TAG_SETTLED_SESSIONS_THRESHOLD sesiones "realizadas" (settled, no
        // canceladas ni la próxima agendada) con la psicóloga que lo recibió — el proceso
        // actual nace con esa derivación, así que sus propias sesiones settled ya son, por
        // definición, las sesiones desde que fue aceptada. El endpoint de lista no expone ese
        // conteo, así que se resuelve acá con el mismo patrón de N+1 por proceso que ya usa
        // MetricsDesempenoTab para getProcessDerivations. Si falla, se sigue mostrando el tag
        // (mismo comportamiento que hoy).
        const referred = mapped.filter((p) => p.isReferred && (p.processId || p.id));
        if (referred.length > 0) {
          try {
            const settledCounts = await Promise.all(
              referred.map((p) =>
                apiCoreAdapter
                  .psychologistPatientSessionsHistory(p.processId || p.id)
                  .then((history) => history.filter((h) => h.status === "settled").length)
                  .catch(() => null)
              )
            );
            if (cancelled) return;

            const suppressedProcessIds = new Set<string>();
            referred.forEach((p, idx) => {
              const settledCount = settledCounts[idx];
              if (settledCount !== null && settledCount >= DERIVATED_TAG_SETTLED_SESSIONS_THRESHOLD) {
                suppressedProcessIds.add(p.processId || p.id);
              }
            });

            if (suppressedProcessIds.size > 0) {
              mapped = mapped.map((p) =>
                p.isReferred && suppressedProcessIds.has(p.processId || p.id)
                  ? { ...p, isReferred: false }
                  : p
              );
            }
          } catch {
            // Si falla el enriquecimiento, seguir mostrando el tag "Derivado" (default actual)
          }
        }

        // Enrich on_hold patients with their motivo (nuevo endpoint psiplatform-be —
        // no viene en patientsInformation de api-core). Solo se pide en esta subtab.
        if (activeTab === "on_hold" && mapped.length > 0) {
          try {
            const { items } = await psiPlatformAdapter.onHoldReasons();
            if (cancelled) return;

            const reasonByProcess = new Map(items.map((it) => [it.process_id, it]));
            mapped = mapped.map((p) => {
              const reasonInfo = reasonByProcess.get(p.processId || p.id);
              if (!reasonInfo) return p;
              return { ...p, onHoldReason: reasonInfo.reason };
            });
          } catch {
            // Si falla, la columna Motivo cae en "—" (ver onHoldReasonCell)
          }
        }

        setPatients(mapped);
      } catch {
        if (!cancelled) {
          setPatients([]);
          toast.error("No se pudo cargar la lista de pacientes");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, typeFilter]);

  const visiblePatients = useMemo(() => {
    let list = patients;
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((p) =>
      p.name.toLowerCase().includes(q) ||
      (p.email && p.email.toLowerCase().includes(q))
    );
    // therapy type already filtered server-side via API
    if (pagoFilter === "pending") list = list.filter((p) => p.paymentStatus === "pending");
    if (pagoFilter === "paid") list = list.filter((p) => p.paymentStatus === "paid");
    if (motivoFilter === "with") list = list.filter((p) => !!p.consultationReason?.trim());
    if (motivoFilter === "without") list = list.filter((p) => !p.consultationReason?.trim());
    return list;
  }, [patients, query, pagoFilter, motivoFilter]);

  const onViewPatient = (patient: Patient) => {
    setDetailPatientInitialTab("info");
    setDetailPatientState(patient);
    writePatientParams(patient.processId || patient.id, "info");
  };

  const onViewPatientDerivation = (patient: Patient) => {
    setDetailPatientInitialTab("derivation");
    setDetailPatientState(patient);
    writePatientParams(patient.processId || patient.id, "derivation");
  };

  const onReadConsultation = (reason: string | undefined, patient: Patient) => {
    setModalReason(reason);
    setModalPatientName(patient.name);
    setModalOpen(true);
  };

  const onOpenNoSchedule = (p: Patient) => {
    const processId = p.processId || p.id;
    const patientName = p.name || "";
    const patientEmail = p.email || "";
    const psychologistEmail = user?.email || "";
    const total = Number.isFinite(p.totalSessions) ? p.totalSessions : 0;
    openNoAgendamientoModal({
      processId,
      patientEmail,
      patientName,
      psychologistEmail,
      totalSessionsGrade: sessionsGrade(total ?? 0),
    });
  };

  const onOpenRefer = (p: Patient) => {
    const processId = p.processId || p.id;
    const total = Number.isFinite(p.totalSessions) ? p.totalSessions : 0;
    const grade = total >= 10 ? "A" : total >= 5 ? "B" : total >= 1 ? "C" : "D";
    // Sin patient_email/patient/psychologist_email en la URL (mismo query string que usaba
    // el iframe de Tally, editable a mano por cualquiera) — Derivaciones.tsx los resuelve
    // autenticado por process_id (ver DerivationSubmissionsService).
    const qs = new URLSearchParams();
    qs.set("process_id", processId);
    qs.set("total_sessions", grade);
    navigate(`/patients/derivation?${qs.toString()}`);
  };

  const onScheduleFromList = (p: Patient) => {
    if (onSchedule) onSchedule(p.processId || p.id);
  };

  const onGoToCalendar = (p: Patient) => {
    if (!p.nextSessionDate) {
      navigate("/calendar");
      return;
    }
    const raw = p.nextSessionDate;
    // Extract YYYY-MM-DD
    const date = raw.split("T")[0].split(" ")[0];
    // Extract HH:MM for viewport scroll (may be UTC — close enough for scrolling)
    const timePart = raw.includes("T") ? raw.split("T")[1] : (raw.split(" ")[1] ?? "");
    const time = timePart ? timePart.substring(0, 5) : null;
    const params = new URLSearchParams({ date });
    if (time) params.set("time", time);
    if (p.email) params.set("openForPatient", p.email);
    navigate(`/calendar?${params.toString()}`);
  };

  const onAddNote = (patient: Patient) => {
    setDetailPatientInitialTab("sessions");
    setDetailPatientAutoOpenNote(true);
    setDetailPatientState(patient);
    writePatientParams(patient.processId || patient.id, "sessions");
  };

  const onViewNotes = (patient: Patient) => {
    setDetailPatientInitialTab("sessions");
    setDetailPatientAutoOpenNote(false);
    setDetailPatientState(patient);
    writePatientParams(patient.processId || patient.id, "sessions");
  };

  const onMarkUrgent = async (patient: Patient) => {
    const processId = patient.processId || patient.id;
    const markedAt = new Date().toISOString();
    // Optimistic update
    setPatients((prev) =>
      prev.map((p) =>
        (p.processId || p.id) === processId
          ? { ...p, isUrgent: true, urgentMarkedAt: markedAt }
          : p
      )
    );
    try {
      await apiCoreAdapter.setPatientUrgency(processId, true);
      activityTracker.trackAction("patient_marked_urgent"); // Métricas de Actividad: solo el hecho, sin el paciente
      toast.success("Paciente marcado como urgente");
      // Notificar al backoffice vía Make.com (fire-and-forget).
      if (URGENT_PATIENTS_WEBHOOK_ENABLED) {
        fetch(URGENT_PATIENTS_WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ process_id: processId, is_urgent: true, patient_name: patient.name }),
        }).catch(() => {/* ignorar errores del webhook */});
      }
      // Mail de confirmación a la psicóloga (fire-and-forget, mismo patrón que el resto de notificaciones de perfil)
      const now = new Date();
      const fechaConfirmacion =
        now.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) +
        ' a las ' +
        now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
      void apiCoreAdapter.psychologistNotifyProfileChanges(
        {
          html: buildUrgentConfirmationEmailHtml({
            firstName: user?.name || '',
            patientName: patient.name,
            fechaConfirmacion,
            contactEmail,
          }),
          subject: 'Confirmación — Paciente marcado como urgente',
          from: 'no-responder@psimammoliti.com',
        },
        { showSuccessToast: false },
      );

      // Umbral de urgentes simultáneos: si al marcar este paciente la psicóloga queda con 3 o
      // más pacientes urgentes ACTIVOS (no histórico), se avisa al equipo y se le informa a
      // ella. Fetch dedicado (no el estado `patients`, que está recortado por tab y por el
      // filtro de tipo de proceso) para contar sobre el universo completo de pacientes.
      // Fire-and-forget: no debe demorar la confirmación de "marcar como urgente".
      void (async () => {
        try {
          const resp = await apiCoreAdapter.patientsInformation({ page: 1, per_page: 800 });
          const rawList = (Array.isArray(resp?.results) ? resp.results : []) as RawPatientResult[];
          const allVisible = mapVisiblePatients(rawList);
          // El fetch puede no reflejar todavía, por consistencia eventual, el is_urgent=true
          // que se acaba de persistir — se fuerza a incluir al paciente recién marcado.
          const urgentPatients = allVisible.filter(
            (p) => p.isUrgent || (p.processId || p.id) === processId,
          );
          const urgentCount = urgentPatients.length;
          if (urgentCount < URGENT_PATIENTS_TEAM_THRESHOLD) return;

          const psychologistName =
            `${user?.name || ''} ${user?.surname || ''}`.trim() || user?.email || 'Psicóloga';
          void apiCoreAdapter.psychologistNotifyProfileChanges(
            {
              html: buildTeamUrgentThresholdEmailHtml({
                psychologistName,
                psychologistEmail: user?.email || '',
                urgentCount,
                patientNames: urgentPatients.map((p) => p.name),
              }),
              subject: 'Aviso — Psicóloga con múltiples pacientes urgentes activos',
              from: 'no-responder@psimammoliti.com',
              audience: 'team',
            },
            { showSuccessToast: false },
          );

          showInfo(
            `Tenés ${urgentCount} pacientes marcados como urgentes en este momento. Le estamos informando la situación a administracion@psimammoliti.com para revisarla junto a vos.`,
            { duration: 8000 },
          );
        } catch {
          // No se pudo verificar el conteo de urgentes activos — no se dispara el aviso al
          // equipo esta vez (se vuelve a evaluar en la próxima acción de marcar urgente).
        }
      })();
    } catch {
      // Revert optimistic update on failure
      setPatients((prev) =>
        prev.map((p) =>
          (p.processId || p.id) === processId
            ? { ...p, isUrgent: false, urgentMarkedAt: undefined }
            : p
        )
      );
      toast.error("No se pudo marcar el paciente como urgente");
    }
  };

  const onUnmarkUrgent = async (patient: Patient) => {
    const processId = patient.processId || patient.id;
    // Optimistic update
    setPatients((prev) =>
      prev.map((p) =>
        (p.processId || p.id) === processId
          ? { ...p, isUrgent: false, urgentMarkedAt: undefined }
          : p
      )
    );
    try {
      await apiCoreAdapter.setPatientUrgency(processId, false);
      toast.success("Paciente desmarcado como urgente");
      // Notificar al backoffice vía Make.com (fire-and-forget). Mismo guard que al marcar.
      if (URGENT_PATIENTS_WEBHOOK_ENABLED) {
        fetch(URGENT_PATIENTS_WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ process_id: processId, is_urgent: false, patient_name: patient.name }),
        }).catch(() => {/* ignorar errores del webhook */});
      }
    } catch {
      // Revert optimistic update on failure
      setPatients((prev) =>
        prev.map((p) =>
          (p.processId || p.id) === processId
            ? { ...p, isUrgent: true, urgentMarkedAt: patient.urgentMarkedAt }
            : p
        )
      );
      toast.error("No se pudo desmarcar el paciente como urgente");
    }
  };

  return {
    activeTab,
    setActiveTab,
    query,
    setQuery,
    page,
    setPage,
    patients,
    loading,
    visiblePatients,
    tabs,
    typeFilter,
    setTypeFilter,
    pagoFilter,
    setPagoFilter,
    motivoFilter,
    setMotivoFilter,
    modalOpen,
    setModalOpen,
    modalReason,
    modalPatientName,
    detailPatient,
    setDetailPatient,
    detailPatientInitialTab,
    detailPatientAutoOpenNote,
    onDetailTabChange,
    onViewPatient,
    onViewPatientDerivation,
    onReadConsultation,
    onOpenNoSchedule,
    onOpenRefer,
    onScheduleFromList,
    onGoToCalendar,
    onAddNote,
    onViewNotes,
    onMarkUrgent,
    onUnmarkUrgent,
    noAgendamientoModalParams,
    closeNoAgendamientoModal,
  };
}

// Keep PatientTable named export accessible for consumers that used to import from PatientsContent
export { PatientTable };
